/**
 * WHICH END of a free sub-span the remainder filler belongs at.
 *
 * A discrete cabinet family (Semi-Custom's 3" grid, Stock's width set) cannot
 * cover an arbitrary span exactly, so `solveModuleSequence` hands back a
 * remainder — up to 3" on the grid, more on a stock set. That strip has to go
 * somewhere, and until now it always went LAST: `packFreeSpan` appended it
 * after the modules, i.e. at the high-param end of the sub-span. High-param is
 * not a design decision — params are measured from the wall's own
 * `alignment.bottomLeft`, so the side a filler lands on was decided by the
 * room polygon's winding. Mirror the kitchen and the fillers move.
 *
 * A filler is a SCRIBE. It reads as intended beside something it can die into
 * — a wall, a corner unit, a tall unit — and reads as a mistake in the middle
 * of a field of doors or hard against the sink. Both ends of a sub-span are
 * known by the time it is packed (a hard piece's `bay.kind`, or a descriptor
 * for the span's outer ends), so the choice can simply be made.
 *
 * Measured over the 21 corpus kitchens before this existed: of 95 lower
 * fillers, 25 sat at the worse end while a better one stood free in the same
 * sub-span — 4 of them hard against a sink base with a bare wall end
 * available. Ranking ratified by the user 2026-09-09.
 *
 * Pure. No geometry, no I/O — two descriptors in, 'lo' | 'hi' out.
 */

/**
 * Bound descriptors, best place for a filler first. The keys are the run's own
 * bay kinds (`bays.js` BAY_KINDS) plus the four a bay cannot name: `opening`
 * (a door/window keep-out), `wall` (the fill zone ends at the wall's own end),
 * `open-end` (the run simply stops here, mid-wall) and `reveal` (a blind or
 * empty corner's reveal filler stands at this end — `cornerReveal.js`).
 */
const FILLER_END_RANK = {
  // A filler is ALREADY there: the remainder joins it, and the leg has one
  // filler instead of two side by side (user ruling 2026-10-26: the corner's
  // 3" is the narrowest it may be, never a second strip beside another).
  reveal: 6,
  // Dies into something solid and is invisible once installed.
  wall: 5,
  corner: 5,
  // A natural vertical break, and the clearance a tall door wants anyway.
  tallUnit: 4,
  // Wants clearance for the door to swing clear of the counter.
  fridge: 3,
  // The cabinetry stops here regardless, so a break reads naturally.
  opening: 2,
  'open-end': 2,
  // Cosmetic only — the counter runs over it — but it is in the field of view.
  range: 1,
  cooktop: 1,
  dishwasher: 1,
  // Worst: a visible strip mid-field, or interrupting the room's focal point.
  'sink-base': 0,
  cabinet: 0,
  filler: 0,
};

/** What an unrecognised descriptor is worth: mid-table, so it never wins or
 * loses a comparison outright against something the table does name. */
const DEFAULT_RANK = 1;

function fillerEndRank(bound) {
  const r = FILLER_END_RANK[bound];
  return r == null ? DEFAULT_RANK : r;
}

/**
 * @param {string} [loBound] descriptor for what bounds the span's LOW end
 * @param {string} [hiBound] descriptor for what bounds the span's HIGH end
 * @returns {'lo'|'hi'} which end the remainder filler goes to. Ties go to
 *   'hi' — the historical trailing position — so a span the caller knows
 *   nothing about packs exactly as it always did.
 */
function preferredFillerEnd(loBound, hiBound) {
  return fillerEndRank(loBound) > fillerEndRank(hiBound) ? 'lo' : 'hi';
}

/**
 * Where a BARE slot shows least — a leftover too narrow to be a filler
 * (`MIN_FILLER_IN`), which no part closes. Not the filler table: a filler is a
 * finished strip and dies best into a corner unit's side, but a bare slot
 * there is a dark line between two fronts (user ruling 2026-10-26, Cabin at
 * 2428 Lore Rd: 0.47" between a blind base and a 15" base, and "we can
 * shorten a run a tiny bit").
 *
 *  - the run's own end — the run is simply that much shorter (against a wall
 *    the counter scribes over it, `scribeCounter.js`) — or a corner reveal,
 *    which takes it in (`cornerReveal.js`);
 *  - beside an appliance, a column or a doorway, where a small gap reads as
 *    the clearance those have anyway;
 *  - anything else: between two cabinet fronts, the one place it shows.
 */
const SLACK_END_RANK = {
  wall: 2,
  'open-end': 2,
  reveal: 2,
  fridge: 1,
  tallUnit: 1,
  range: 1,
  cooktop: 1,
  dishwasher: 1,
  opening: 1,
  clearance: 1,
};
const slackEndRank = (bound) => SLACK_END_RANK[bound] ?? 0;

/**
 * Which end of a free sub-span a sub-filler leftover goes to: the one where a
 * slot shows less; on a tie 'hi', the trailing position the packer has always
 * left it at.
 */
function preferredSlackEnd(loBound, hiBound) {
  return slackEndRank(loBound) > slackEndRank(hiBound) ? 'lo' : 'hi';
}

module.exports = {
  preferredFillerEnd,
  preferredSlackEnd,
  fillerEndRank,
  FILLER_END_RANK,
};
